/*
 * Search Inventory Table Form
 * searching inventory has some special dropdowns for locations
 */

import * as React from 'react';

import { AbstractControl, FieldConfig, FormGroup, GroupProps } from 'react-reactive-form';
import { Button, Row } from 'react-bootstrap';
import Select from 'react-select';
import {
    Ibuilding,
    Ifacility,
    Ifloor,
    IinstallBasePopulated,
    Ijob,
    Ilocation,
    Ioption,
    IproductInfo,
    ItableFiltersReducer
} from '../../models';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import omit from 'lodash/omit';
import { jobTypesIdEnum, inventoryStatusFilterEnum, workOrderStatusEnum } from '../../models-enums';

import { FormUtil } from '../common/FormUtil';
import Icon from '../common/Icon';
import FilterDropdown from '../common/FilterDropdown';
import MultiSelectFilterDropdown from '../common/MultiSelectFilterDropdown';
import { IsimpleOption, sortOptionsNatural } from '../common/multiSelectFilterUtil';
import { InventoryActionButtonContainer } from './InventoryActionButtonContainer';
import { RouteComponentProps } from 'react-router';
import { constants } from '../../constants/constants';
import { setTableFilter } from '../../actions/manageLocationActions';
import { setSelectedCart } from '../../actions/partsActions';
import { setFilterConfig } from '../../actions/manageInventoryActions';
import {
    getStatusFilterKey,
    getStatusOrder,
    InventoryStatusFilterKey,
    shouldShowStatusFilter
} from './inventoryStatusFilterUtil';
import { WithTranslation } from 'react-i18next';

const STATUS_COLORS = {
    GREEN: '#4CAF50',
    RED: '#D32F2F',
    BLUE: '#2196F3',
    GREY: '#9E9E9E'
} as const;

const defaultBuildingID = { value: 'unallocated', label: 'Unallocated' };

interface Iprops extends RouteComponentProps<Record<string, never>>, WithTranslation {
    handleSubmit: any;
    loading: boolean;
    showSearchButton?: boolean;
    colorButton: string;
    facility: Ifacility;
    buildings: Ibuilding[];
    setTableFilter: typeof setTableFilter;
    tableFilters: ItableFiltersReducer;
    productInfo: IproductInfo;
    startAddWorkOrders: () => void;
    clearTableFilter: () => void;
    setFilterConfig: typeof setFilterConfig;
    installBasesPopulated: IinstallBasePopulated[];
    isWorkOrderTypeJob: boolean;
    isAddWorkOrderMode: boolean;
    isJobDownloaded: boolean;
    selectedJob: Ijob;
    hasSelection: boolean;
    resetSelection: () => void;
    setSelectedCart: typeof setSelectedCart;
}

interface Istate {
    fieldConfig: FieldConfig;
    searchValue: string;
}

export default class SearchInventoryTableForm extends React.Component<Iprops, Istate> {
    public searchForm: FormGroup | any;
    private subscription: any;
    private showBtn: boolean;

    constructor(props: Iprops) {
        super(props);
        this.state = {
            fieldConfig: { controls: {} },
            searchValue: props.tableFilters.search || ''
        };
        this.showBtn = typeof this.props.showSearchButton === 'undefined' ? true : this.props.showSearchButton;
        this.props.setTableFilter({
            page: 0,
            search: '',
            building: null,
            floor: null,
            location: null,
            rooms: [],
            hideVirtualDecommissionedToggle: this.props.tableFilters.hideVirtualDecommissionedToggle || false
        });
    }

    componentDidMount() {
        const params = new URLSearchParams(this.props.location.search);
        if (params && params.get('switch_job')) {
            this.props.clearTableFilter();
            this.props.history.replace('/devices');
            setTimeout(() => {
                this.setState({
                    fieldConfig: FormUtil.translateForm(this.buildSearchControls(), this.props.t)
                });
            }, 100);
        } else {
            this.setState({
                fieldConfig: FormUtil.translateForm(this.buildSearchControls(), this.props.t)
            });
        }

        this.props.setFilterConfig(FormUtil.translateForm(this.buildSearchControls(), this.props.t));

        if (this.props.i18n) {
            this.props.i18n.on('languageChanged', this.handleLanguageChanged);
        }
    }

    componentDidUpdate(prevProps: Iprops) {
        if (JSON.stringify(prevProps.buildings) !== JSON.stringify(this.props.buildings)) {
            this.setState({
                fieldConfig: FormUtil.translateForm(this.buildSearchControls(), this.props.t)
            });
        }
        if (prevProps.tableFilters.showAdditionalFilters !== this.props.tableFilters.showAdditionalFilters) {
            this.setState({
                fieldConfig: FormUtil.translateForm(this.buildSearchControls(), this.props.t)
            });
        }
        if (prevProps.isAddWorkOrderMode !== this.props.isAddWorkOrderMode) {
            this.setState({
                fieldConfig: FormUtil.translateForm(this.buildSearchControls(), this.props.t)
            });
        }
        // Sync search value when cleared externally
        if (prevProps.tableFilters.search !== this.props.tableFilters.search) {
            this.setState({ searchValue: this.props.tableFilters.search || '' });
        }
    }

    componentWillUnmount() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }

        if (this.props.i18n) {
            this.props.i18n.off('languageChanged', this.handleLanguageChanged);
        }
    }

    subscribeToValueChanges = () => {
        forEach(this.state.fieldConfig.controls, (input: any, key) => {
            if (key.indexOf('$field') !== -1) {
                return;
            }
            this.subscription = this.searchForm.get(key).valueChanges.subscribe((value: any) => {
                this.onValueChanges(value, key);
            });
        });
    };

    buildSearchControls = (): FieldConfig => {
        const {
            building,
            floor,
            location,
            rooms,
            measurementPointListResultStatus,
            workOrderResultStatus,
            mainCategory,
            showAdditionalFilters,
            search,
            system
        } = this.props.tableFilters;

        const disabled = false;
        const foundBuilding = find(this.props.buildings, (b: Ibuilding) => {
            return building && b.id === building.value;
        });
        if (building && !foundBuilding) {
            this.props.clearTableFilter();
        }
        let floorOptions;
        let locationOptions;
        let roomOptions;

        const buildingOptions = FormUtil.convertToOptions([...this.props.buildings, defaultBuildingID]);

        if (foundBuilding && typeof foundBuilding === 'object' && 'floors' in foundBuilding && foundBuilding.floors) {
            floorOptions = FormUtil.convertToOptions(foundBuilding.floors);
            if (floor) {
                const foundFloor = find(foundBuilding.floors, (f: Ifloor) => {
                    return f.id === floor.value;
                });
                if (foundFloor) {
                    locationOptions = FormUtil.convertToOptions(foundFloor.locations);
                    if (location) {
                        const foundLocation = find(foundFloor.locations, (l: Ilocation) => {
                            return l.id === location.value;
                        });
                        if (foundLocation) {
                            roomOptions = FormUtil.convertToOptions(foundLocation.rooms);
                        }
                    }
                }
            }
        }

        const measurementPointListResultStatusOptions = [...FormUtil.convertEnumToOptions(inventoryStatusFilterEnum)];
        const workOrderResultStatusOptions = FormUtil.convertEnumToOptions(workOrderStatusEnum).map(item => {
            return {
                value: item.value,
                label: this.props.t('manageWorkOrder:' + item.label)
            };
        });

        const { t } = this.props;

        let mainSearchControls = {
            building: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: t('common:building'),
                    options: buildingOptions,
                    colWidth: 5,
                    colWidthLarge: 4,
                    type: 'text',
                    placeholder: 'searchPlaceholder',
                    isClearable: true
                },
                formState: { value: building, disabled }
                // TODO figure out how to pre-select unallocated
                // formState: { value: building || defaultBuildingID, disabled }
            },
            floor: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: t('floor'),
                    colWidth: 4,
                    colWidthLarge: 4,
                    placeholder: 'common:searchPlaceholder',
                    options: floorOptions,
                    isClearable: true
                },
                formState: { value: floor, disabled }
            }
        } as { [key: string]: GroupProps };

        let additionalSearchControls = {
            location: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: t('location'),
                    colWidth: 4,
                    colWidthLarge: 2,
                    placeholder: 'common:searchPlaceholder',
                    options: locationOptions,
                    isClearable: true
                },
                formState: { value: location, disabled }
            },
            room: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: t('room'),
                    colWidth: 4,
                    colWidthLarge: 2,
                    placeholder: 'common:searchPlaceholder',
                    options: roomOptions,
                    isClearable: true,
                    isMulti: true
                },
                formState: { value: rooms, disabled }
            },

            measurementPointListResultStatus: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: t('status'),
                    options: measurementPointListResultStatusOptions,
                    colWidth: 4,
                    colWidthLarge: 3,
                    type: 'select',
                    placeholder: 'measurementPointListResultStatusPlaceholder',
                    isClearable: true,
                    shouldTranslate: true
                },
                formState: {
                    value: measurementPointListResultStatus,
                    disabled
                }
            },
            workOrderResultStatus: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: t('manageWorkOrder:workOrderResultStatus'),
                    options: workOrderResultStatusOptions,
                    colWidth: 4,
                    colWidthLarge: 3,
                    type: 'select',
                    placeholder: t('manageWorkOrder:workOrderResultStatusPlaceholder'),
                    isClearable: true,
                    shouldTranslate: true
                },
                formState: { value: workOrderResultStatus, disabled }
            },
            mainCategory: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: t('common:mainCategory'),
                    options: this.props.productInfo.mainCategoryOptions,
                    colWidth: 5,
                    colWidthLarge: 4,
                    type: 'select',
                    placeholder: t('searchPlaceholder'),
                    isClearable: true,
                    shouldTranslate: false
                },
                formState: { value: mainCategory, disabled }
            },
            search: {
                render: FormUtil.TextInputWithoutValidation,
                meta: {
                    label: t('common:search'),
                    colWidth: 4,
                    type: 'input',
                    placeholder: 'tableFilters.searchPlaceholder',
                    name: 'asset-search'
                },
                formState: { value: search, disabled }
            },
            system: {
                render: FormUtil.Select,
                meta: {
                    options: constants.constructSystems(this.props.t),
                    label: t('manageInventory:system'),
                    colWidth: 4,
                    placeholder: 'nsJob:typePlaceholder',
                    name: 'system',
                    disableSort: true,
                    isClearable: true
                },
                formState: { value: system, disabled }
            },
            originalMeasurementPointListResultStatus: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: measurementPointListResultStatusOptions.map(({ value, label }) => ({
                        value,
                        label: this.props.t('manageMeasurementPointLists:' + label)
                    })),
                    label: t('status'),
                    colWidth: 4,
                    placeholder: 'Search',
                    name: 'originalMeasurementPointListResultStatus',
                    isClearable: true
                },
                formState: { value: undefined, disabled }
            },
            hideVirtualDecommissionedToggle: {
                render: FormUtil.Toggle,
                meta: {
                    label: t('common:hideVirtual'),
                    colWidth: 4,
                    style: {
                        marginTop: '2%'
                    }
                },
                formState: {
                    value: this.props.tableFilters.hideVirtualDecommissionedToggle,
                    disabled
                },
                value: this.props.tableFilters.hideVirtualDecommissionedToggle
            }
        } as { [key: string]: GroupProps };

        // which status select
        if (this.props.isAddWorkOrderMode === true) {
            additionalSearchControls = omit(additionalSearchControls, [
                'workOrderResultStatus',
                'measurementPointListResultStatus'
            ]);
        } else {
            mainSearchControls = omit(mainSearchControls, ['$field_1']);
            additionalSearchControls = omit(additionalSearchControls, ['originalMeasurementPointListResultStatus']);
        }

        if (this.props.isWorkOrderTypeJob && this.props.selectedJob.jobTypeID !== jobTypesIdEnum.commissioning) {
            additionalSearchControls = omit(additionalSearchControls, 'measurementPointListResultStatus');
        } else {
            additionalSearchControls = omit(
                additionalSearchControls,
                'workOrderResultStatus',
                'originalMeasurementPointListResultStatus'
            );
        }

        if (this.props.selectedJob.jobTypeID !== jobTypesIdEnum.agsRebalancing) {
            additionalSearchControls = omit(additionalSearchControls, 'system');
        }

        const filterParams: any = {
            building,
            floor,
            location,
            rooms,
            measurementPointListResultStatus,
            workOrderResultStatus,
            mainCategory,
            showAdditionalFilters,
            search,
            system,
            hideVirtualDecommissionedToggle: this.props.tableFilters.hideVirtualDecommissionedToggle || false
        };

        // Remove undefined values to satisfy exactOptionalPropertyTypes
        Object.keys(filterParams).forEach(key => {
            if (filterParams[key] === undefined) {
                delete filterParams[key];
            }
        });

        this.props.setTableFilter(filterParams);

        if (showAdditionalFilters) {
            return {
                controls: {
                    ...mainSearchControls,
                    ...additionalSearchControls
                }
            };
        } else {
            return { controls: { ...mainSearchControls } };
        }
    };

    debouncedSetTableFilter = debounce((key, value) => {
        this.props.setTableFilter({ [key]: value, page: 0 });
    }, 500);

    /*
     * (reusable)
     * set the table filters to redux on each value change
     */
    onValueChanges = (value: any, key: string) => {
        switch (key) {
            case 'building':
                this.filterFloors(value);
                break;
            case 'location':
                this.filterRooms(value);
                break;
            case 'floor':
                this.filterLocations(value);
                break;
            case 'search':
                this.debouncedSetTableFilter(key, value);

                break;
            default:
                this.props.setTableFilter({ [key]: value, page: 0 });
                break;
        }
    };

    filterFloors = (value: Ioption | null) => {
        // Reset dependent filters when building changes
        this.props.setTableFilter({
            page: 0,
            floor: null,
            location: null,
            rooms: [],
            building: value
        });
    };

    filterLocations = (value: Ioption | null) => {
        // Reset dependent filters when floor changes
        this.props.setTableFilter({
            page: 0,
            floor: value,
            location: null,
            rooms: []
        });
    };

    filterRooms = (value: Ioption | null) => {
        // Reset dependent filters when location changes
        this.props.setTableFilter({
            page: 0,
            location: value,
            rooms: []
        });
    };

    // Commit the staged room multi-select on Apply / clear.
    handleRoomsApply = (selectedRooms: IsimpleOption[]) => {
        this.props.setTableFilter({ rooms: selectedRooms, page: 0 });
    };

    handleToggleAdditionalFilters = () => {
        this.props.setTableFilter({
            showAdditionalFilters: !this.props.tableFilters.showAdditionalFilters
        });
    };

    hasActiveAdditionalFilters = () => {
        const { mainCategory } = this.props.tableFilters;
        if (mainCategory) {
            return true;
        } else {
            return false;
        }
    };

    handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Desktop filters handle their own onChange events, no form submission needed
        if (this.searchForm && this.searchForm.value) {
            this.props.handleSubmit(this.searchForm.value);
        }
    };

    setForm = (form: AbstractControl) => {
        this.searchForm = form;
        this.searchForm.meta = {
            loading: this.props.loading
        };
        this.subscribeToValueChanges();
    };

    handleLanguageChanged = () => {
        this.setState({
            fieldConfig: FormUtil.translateForm(this.buildSearchControls(), this.props.t)
        });
    };

    render() {
        return (
            <Row className="search-table-form beacon-form inventory-filter-form">
                <form onSubmit={this.handleSubmit} className="inventory-filter-desktop">
                    {this.renderDesktopFilters()}
                </form>
            </Row>
        );
    }

    renderDesktopFilters = () => {
        const { t, tableFilters, loading, isAddWorkOrderMode } = this.props;
        
        return (
            <>
                <div className="input-with-icon search">
                    <Icon name="search" />
                    <input
                        type="text"
                        placeholder="Search for Product/Asset.."
                        value={this.state.searchValue}
                        disabled={loading}
                        onChange={e => this.handleSearchChange(e.target.value)}
                    />
                    {this.state.searchValue.length > 0 && (
                        <button
                            type="button"
                            className="clear-search-btn"
                            aria-label={t('common:clear')}
                            disabled={loading}
                            onClick={this.handleClearSearch}
                        >
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M3.59946 2.34311C3.25233 1.99598 2.68951 1.99598 2.34238 2.34311C1.99525 2.69025 1.99525 3.25306 2.34238 3.60019L6.74216 7.99997L2.34238 12.3997C1.99525 12.7469 1.99525 13.3097 2.34238 13.6568C2.68951 14.004 3.25233 14.004 3.59946 13.6568L7.99924 9.25705L12.399 13.6568C12.7461 14.004 13.309 14.004 13.6561 13.6568C14.0032 13.3097 14.0032 12.7469 13.6561 12.3997L9.25631 7.99997L13.6561 3.60019C14.0032 3.25306 14.0032 2.69025 13.6561 2.34311C13.309 1.99598 12.7461 1.99598 12.399 2.34311L7.99923 6.74289L3.59946 2.34311Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </button>
                    )}
                </div>

                <FilterDropdown
                    value={tableFilters.mainCategory?.value ?? null}
                    options={this.getCategoryOptions()}
                    onChange={(value) => this.handleFilterDropdownChange('mainCategory', value)}
                    placeholder="Category: All"
                    labelPrefix="Category: "
                    disabled={loading}
                />
                
                {this.getShowStatusFilter() && (
                    <FilterDropdown
                        value={this.getStatusValue()}
                        options={this.getStatusOptions()}
                        onChange={(value) => this.handleFilterDropdownChange(this.getActiveStatusFilterKey(), value)}
                        placeholder="Status: All"
                        labelPrefix="Status: "
                        renderOption={this.renderStatusOption}
                        renderSelectedValue={this.renderStatusOption}
                        disabled={loading}
                    />
                )}
                
                <FilterDropdown
                    value={tableFilters.building?.value ?? null}
                    options={this.getFormattedBuildingOptions()}
                    onChange={(value) => this.handleFilterDropdownChange('building', value)}
                    placeholder="Bldg: All"
                    labelPrefix="Bldg: "
                    disabled={loading}
                />
                
                <FilterDropdown
                    value={tableFilters.floor?.value ?? null}
                    options={this.getFormattedFloorOptions()}
                    onChange={(value) => this.handleFilterDropdownChange('floor', value)}
                    placeholder="Floor: All"
                    labelPrefix="Floor: "
                    disabled={loading || this.getFloorOptions().length === 0}
                />
                
                <FilterDropdown
                    value={tableFilters.location?.value ?? null}
                    options={this.getFormattedLocationOptions()}
                    onChange={(value) => this.handleFilterDropdownChange('location', value)}
                    placeholder="Location: All"
                    labelPrefix="Location: "
                    disabled={loading || this.getLocationOptions().length === 0}
                />
                
                <MultiSelectFilterDropdown
                    values={tableFilters.rooms ?? []}
                    options={this.getFormattedRoomOptions()}
                    onApply={this.handleRoomsApply}
                    placeholder="Room: All"
                    labelPrefix="Room: "
                    searchPlaceholder="Search rooms..."
                    emptyLabel="No rooms"
                    disabled={loading || this.getRoomOptions().length === 0}
                />
                
                {this.props.selectedJob.jobTypeID === jobTypesIdEnum.agsRebalancing && (
                    <FilterDropdown
                        value={tableFilters.system?.value?.toString() ?? null}
                        options={this.getFormattedSystemOptions()}
                        onChange={(value) => this.handleFilterDropdownChange('system', value)}
                        placeholder="System: All"
                        labelPrefix="System: "
                        disabled={loading}
                    />
                )}

                {this.hasActiveFilters() && (
                    <Button type="button" disabled={loading} onClick={this.handleClearFilters} variant="default" className="clear-filters-btn">
                        {t('common:clear')}
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M3.59946 2.34311C3.25233 1.99598 2.68951 1.99598 2.34238 2.34311C1.99525 2.69025 1.99525 3.25306 2.34238 3.60019L6.74216 7.99997L2.34238 12.3997C1.99525 12.7469 1.99525 13.3097 2.34238 13.6568C2.68951 14.004 3.25233 14.004 3.59946 13.6568L7.99924 9.25705L12.399 13.6568C12.7461 14.004 13.309 14.004 13.6561 13.6568C14.0032 13.3097 14.0032 12.7469 13.6561 12.3997L9.25631 7.99997L13.6561 3.60019C14.0032 3.25306 14.0032 2.69025 13.6561 2.34311C13.309 1.99598 12.7461 1.99598 12.399 2.34311L7.99923 6.74289L3.59946 2.34311Z"
                                fill="currentColor"
                            />
                        </svg>
                    </Button>
                )}

                {isAddWorkOrderMode && (
                    <>
                        <Button
                            variant="outline-primary"
                            onClick={this.props.startAddWorkOrders}
                            disabled={this.props.hasSelection === false}
                            title={this.props.hasSelection ? 'add' : 'must select at least one'}
                        >
                            {t('addSelectedAssetToJob')}
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={this.props.resetSelection}
                            disabled={this.props.hasSelection === false}
                            title={this.props.hasSelection ? 'reset' : 'must select at least one'}
                        >
                            {t('resetSelectionButton')}
                        </Button>
                    </>
                )}

                {isAddWorkOrderMode === false && (
                    <InventoryActionButtonContainer
                        t={t}
                        facility={this.props.facility}
                        history={this.props.history}
                        location={this.props.location}
                        match={this.props.match}
                        installBasesPopulated={this.props.installBasesPopulated}
                        colorButton={this.props.colorButton}
                        resetSelection={this.props.resetSelection}
                        hideVirtualDecommissioned={tableFilters.hideVirtualDecommissionedToggle || false}
                        onHideVirtualChange={this.handleHideVirtualChange}
                    />
                )}
            </>
        );
    }

    renderFilterDropdown = (filterKey: string, options: Ioption[]) => {
        const { tableFilters } = this.props;
        const value = tableFilters[filterKey as keyof ItableFiltersReducer] as Ioption | null;
        
        const labelMap: { [key: string]: string } = {
            building: 'Bldg',
            floor: 'Floor',
            location: 'Location',
            room: 'Room',
            mainCategory: 'Category',
            system: 'System'
        };

        const label = labelMap[filterKey] || filterKey;

        return (
            <div className="filter-dropdown-wrapper" key={filterKey}>
                <Select
                    classNamePrefix="react-select"
                    className="react-select-container inventory-filter-select"
                    options={options}
                    placeholder={`${label}: All`}
                    onChange={(option) => this.handleDropdownChange(filterKey, option, value)}
                    value={value}
                    isClearable={false}
                />
            </div>
        );
    }

    getStatusColor = (statusValue: string): string => {
        const value = parseInt(statusValue);

        // Work Order Status Colors (semantic mapping)
        if (this.getActiveStatusFilterKey() === 'workOrderResultStatus') {
            if (value === workOrderStatusEnum.complete) return STATUS_COLORS.GREEN;
            if (value === workOrderStatusEnum.reopened) return STATUS_COLORS.RED;
            if (value === workOrderStatusEnum.new) return STATUS_COLORS.BLUE;
            // open, notApplicable
            return STATUS_COLORS.GREY;
        }
        
        // Measurement Point Status Colors
        if (value === inventoryStatusFilterEnum.resultStatusPass) return STATUS_COLORS.GREEN;
        if (value === inventoryStatusFilterEnum.resultStatusFail) return STATUS_COLORS.RED;
        if (value === inventoryStatusFilterEnum.resultStatusNotTested) return STATUS_COLORS.BLUE;
        return STATUS_COLORS.GREY;
    }

    formatStatusOptionLabel = (option: Ioption) => {
        const color = this.getStatusColor(option.value);
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0
                }} />
                <span>{option.label}</span>
            </div>
        );
    }

    renderStatusDropdown = () => {
        const { t, tableFilters, isWorkOrderTypeJob, isAddWorkOrderMode, selectedJob } = this.props;
        
        if (isAddWorkOrderMode) {
            return null;
        }

        let options: Ioption[] = [];
        let value: Ioption | null = null;
        let label = 'Status';

        if (isWorkOrderTypeJob && selectedJob.jobTypeID !== jobTypesIdEnum.commissioning) {
            options = FormUtil.convertEnumToOptions(workOrderStatusEnum).map(item => ({
                value: item.value,
                label: t('manageWorkOrder:' + item.label)
            }));
            value = tableFilters.workOrderResultStatus || null;
        } else {
            options = FormUtil.convertEnumToOptions(inventoryStatusFilterEnum).map(item => ({
                value: item.value,
                label: t('manageInventory:' + item.label)
            }));
            // Sort options: Pass, Fail, Not Tested, then others
            options.sort((a, b) => getStatusOrder(a.value) - getStatusOrder(b.value));
            value = tableFilters.measurementPointListResultStatus || null;
        }

        return (
            <div className="filter-dropdown-wrapper" key="status">
                <Select
                    classNamePrefix="react-select"
                    className="react-select-container inventory-filter-select"
                    options={options}
                    placeholder={`${label}: All`}
                    onChange={(option) => this.handleDropdownChange(isWorkOrderTypeJob ? 'workOrderResultStatus' : 'measurementPointListResultStatus', option, value)}
                    value={value}
                    formatOptionLabel={this.formatStatusOptionLabel}
                    isClearable={false}
                />
            </div>
        );
    }

    handleDropdownChange = (filterKey: string, option: Ioption | null, currentValue: Ioption | null) => {
        // If clicking on the already selected item, deselect it
        if (option && currentValue && option.value === currentValue.value) {
            this.onValueChanges(null, filterKey);
        } else if (!option) {
            this.onValueChanges(null, filterKey);
        } else {
            this.onValueChanges(option, filterKey);
        }
    }

    handleSearchChange = (value: string) => {
        this.setState({ searchValue: value });
        this.onValueChanges(value, 'search');
    }

    // Clear only the search term, leaving any other active filters intact.
    handleClearSearch = () => {
        // Cancel any pending debounced search write so it can't re-apply the old term.
        this.debouncedSetTableFilter.cancel();
        this.setState({ searchValue: '' });
        this.props.setTableFilter({ search: '', page: 0 });
    }

    handleClearFilters = () => {
        this.setState({ searchValue: '' });
        // Explicitly clear all filter fields by setting them to null or omitting them
        this.props.setTableFilter({
            search: '',
            building: null,
            floor: null,
            location: null,
            rooms: [],
            page: 0
        });
        // Then update to clear the optional fields by calling again without them
        // This ensures mainCategory, system, and status fields are also cleared
        this.onValueChanges(null, 'mainCategory');
        this.onValueChanges(null, 'system');
        this.onValueChanges(null, 'measurementPointListResultStatus');
        this.onValueChanges(null, 'workOrderResultStatus');
        this.onValueChanges(null, 'originalMeasurementPointListResultStatus');
    }

    handleHideVirtualChange = (checked: boolean) => {
        this.props.setTableFilter({ hideVirtualDecommissionedToggle: checked, page: 0 });
    }

    hasActiveFilters = () => {
        const { tableFilters } = this.props;
        return !!(
            tableFilters.search ||
            tableFilters.building ||
            tableFilters.floor ||
            tableFilters.location ||
            (tableFilters.rooms && tableFilters.rooms.length) ||
            tableFilters.measurementPointListResultStatus ||
            tableFilters.workOrderResultStatus ||
            tableFilters.originalMeasurementPointListResultStatus ||
            tableFilters.mainCategory ||
            tableFilters.system
        );
    }

    getCategoryOptions = (): Array<{ value: string; label: string }> => {
        const categoryOptions = this.props.productInfo.mainCategoryOptions || [];
        // Return options without "Category: " prefix - the label separation happens in the toggle display
        return categoryOptions.map(option => ({
            value: option.value,
            label: option.label
        }));
    }

    getActiveStatusFilterKey = (): InventoryStatusFilterKey => {
        const { isAddWorkOrderMode, isWorkOrderTypeJob, selectedJob } = this.props;
        return getStatusFilterKey(isAddWorkOrderMode, isWorkOrderTypeJob, selectedJob.jobTypeID);
    }

    getShowStatusFilter = (): boolean => {
        const { isAddWorkOrderMode, isWorkOrderTypeJob, selectedJob } = this.props;
        return shouldShowStatusFilter(isAddWorkOrderMode, isWorkOrderTypeJob, selectedJob.jobTypeID);
    }

    getStatusValue = (): string | null => {
        return this.props.tableFilters[this.getActiveStatusFilterKey()]?.value ?? null;
    }

    getStatusOptions = (): Array<{ value: string; label: string }> => {
        const { t } = this.props;
        const statusFilterKey = this.getActiveStatusFilterKey();

        if (statusFilterKey === 'workOrderResultStatus') {
            return FormUtil.convertEnumToOptions(workOrderStatusEnum).map(item => ({
                value: item.value,
                label: t('manageWorkOrder:' + item.label)
            }));
        } else {
            const options = FormUtil.convertEnumToOptions(inventoryStatusFilterEnum)
                // the original status filter matches by exact status, so the derived
                // "Not Tested Last 12M" option does not apply there
                .filter(
                    item =>
                        statusFilterKey !== 'originalMeasurementPointListResultStatus' ||
                        item.value !== inventoryStatusFilterEnum.resultStatusNotTestedLast12M
                )
                .map(item => ({
                    value: item.value,
                    label:
                        statusFilterKey === 'originalMeasurementPointListResultStatus'
                            ? t('manageMeasurementPointLists:' + item.label)
                            : t('manageInventory:' + item.label)
                }));
            // Sort options: Pass, Fail, Not Tested, then others
            return options.sort((a, b) => getStatusOrder(a.value) - getStatusOrder(b.value));
        }
    }

    renderStatusOption = (option: { value: string; label: string }) => {
        const color = this.getStatusColor(option.value);
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0
                }} />
                <span>{option.label}</span>
            </div>
        );
    }

    getFormattedBuildingOptions = (): Array<{ value: string; label: string }> => {
        return this.getBuildingOptions().map(option => ({
            value: option.value,
            label: option.label
        }));
    }

    getFormattedFloorOptions = (): Array<{ value: string; label: string }> => {
        return this.getFloorOptions().map(option => ({
            value: option.value,
            label: option.label
        }));
    }

    getFormattedLocationOptions = (): Array<{ value: string; label: string }> => {
        return this.getLocationOptions().map(option => ({
            value: option.value,
            label: option.label
        }));
    }

    getFormattedRoomOptions = (): Array<{ value: string; label: string }> => {
        return this.getRoomOptions().map(option => ({
            value: option.value,
            label: option.label
        }));
    }

    getFormattedSystemOptions = (): Array<{ value: string; label: string }> => {
        const { t } = this.props;
        return constants.constructSystems(t).map(option => ({
            value: option.value.toString(),
            label: option.label
        }));
    }

    handleFilterDropdownChange = (filterKey: string, value: string | null) => {
        if (!value || value === 'All') {
            // Clear the filter
            this.onValueChanges(null, filterKey);
            return;
        }

        // Find the appropriate option based on filter type
        let option: Ioption | null = null;

        switch (filterKey) {
            case 'mainCategory':
                option = this.props.productInfo.mainCategoryOptions?.find(opt => opt.value === value) || null;
                break;
            case 'measurementPointListResultStatus':
            case 'workOrderResultStatus':
            case 'originalMeasurementPointListResultStatus': {
                // Use the already-formatted status options (includes translation)
                const statusOptions = this.getStatusOptions();
                // Compare as strings since value comes in as string
                const statusOption = statusOptions.find(opt => opt.value.toString() === value);
                if (statusOption) {
                    option = { value: statusOption.value, label: statusOption.label };
                }
                break;
            }
            case 'building':
                option = this.getBuildingOptions().find(opt => opt.value === value) || null;
                break;
            case 'floor':
                option = this.getFloorOptions().find(opt => opt.value === value) || null;
                break;
            case 'location':
                option = this.getLocationOptions().find(opt => opt.value === value) || null;
                break;
            case 'system': {
                const systemOption = constants.constructSystems(this.props.t).find(opt => opt.value.toString() === value);
                option = systemOption ? { value: systemOption.value, label: systemOption.label } : null;
                break;
            }
            default:
                break;
        }

        if (option) {
            this.onValueChanges(option, filterKey);
        } else {
            // Fallback: if option not found, clear the filter
            this.onValueChanges(null, filterKey);
        }
    }

    getBuildingOptions = (): Ioption[] => {
        return sortOptionsNatural(FormUtil.convertToOptions([...this.props.buildings, defaultBuildingID]));
    }

    getFloorOptions = (): Ioption[] => {
        const { building } = this.props.tableFilters;
        if (!building || building.value === 'all') return [];
        
        const foundBuilding = find(this.props.buildings, (b: Ibuilding) => b.id === building.value);
        if (foundBuilding && foundBuilding.floors) {
            return sortOptionsNatural(FormUtil.convertToOptions(foundBuilding.floors));
        }
        return [];
    }

    getLocationOptions = (): Ioption[] => {
        const { building, floor } = this.props.tableFilters;
        if (!building || !floor || floor.value === 'all') return [];
        
        const foundBuilding = find(this.props.buildings, (b: Ibuilding) => b.id === building.value);
        if (foundBuilding && foundBuilding.floors) {
            const foundFloor = find(foundBuilding.floors, (f: Ifloor) => f.id === floor.value);
            if (foundFloor && foundFloor.locations) {
                return sortOptionsNatural(FormUtil.convertToOptions(foundFloor.locations));
            }
        }
        return [];
    }

    getRoomOptions = (): Ioption[] => {
        const { building, floor, location } = this.props.tableFilters;
        if (!building || !floor || !location || location.value === 'all') return [];
        
        const foundBuilding = find(this.props.buildings, (b: Ibuilding) => b.id === building.value);
        if (foundBuilding && foundBuilding.floors) {
            const foundFloor = find(foundBuilding.floors, (f: Ifloor) => f.id === floor.value);
            if (foundFloor && foundFloor.locations) {
                const foundLocation = find(foundFloor.locations, (l: Ilocation) => l.id === location.value);
                if (foundLocation && foundLocation.rooms) {
                    return sortOptionsNatural(FormUtil.convertToOptions(foundLocation.rooms));
                }
            }
        }
        return [];
    }
}
